import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";

// Founding memorials — protected from grant/revoke/delete in the admin UI.
// Must match FOUNDING_PREMIUM_SLUGS in backend/server.py.
const FOUNDER_SLUGS = new Set([
  "f8862626-abby",
  "d50e0abf-little-bear",
  "e8f56ae1-princess",
  "c0ffeead-memito",
  "08112026-mickey",
]);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [me, setMe] = useState(null);

  useEffect(() => {
    apiClient.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {
      localStorage.removeItem("rb_admin_token");
      nav("/admin/login");
    });
  }, [nav]);



  const logout = () => {
    localStorage.removeItem("rb_admin_token");
    localStorage.removeItem("rb_admin_email");
    nav("/admin/login");
  };

  if (!me) return null;

  return (
    <main className="relative max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="tracking-celestial text-[var(--rb-gold)] mb-2">The studio</div>
          <h1 className="font-serif-display text-4xl text-[var(--rb-text-heading)]">Welcome, artisan.</h1>
          <div className="text-xs text-[var(--rb-text-muted)] mt-1">{me.email}</div>
        </div>
        <div className="flex items-center gap-3">
          <PayPalModeBadge />
          <button onClick={logout} className="btn-ghost" data-testid={ADMIN.logoutBtn}>Sign out</button>
        </div>
      </div>

      <nav className="flex flex-wrap gap-2 mb-8 border-b border-[var(--rb-gold-soft)]">
        {[
          { id: "inquiries", label: "Inquiries", testid: "admin-nav-inquiries" },
          { id: "analytics", label: "Analytics", testid: "admin-nav-analytics" },
          { id: "designs", label: "Custom Designs", testid: ADMIN.navDesigns },
          { id: "memorials", label: "Public Memorials", testid: ADMIN.navMemorials },
          { id: "ecards", label: "eCards Sent", testid: ADMIN.navEcards },
          { id: "tools", label: "Studio Tools", testid: "admin-nav-tools" },
          { id: "keepsakes", label: "Keepsake Cards", testid: "admin-nav-keepsakes", href: "/admin/keepsake-cards" },
        ].map((t) => (
          t.href ? (
            <a
              key={t.id}
              href={t.href}
              data-testid={t.testid}
              className="px-5 py-3 text-[11px] tracking-[0.22em] uppercase transition-all border-b-2 border-transparent text-[var(--rb-text-muted)] hover:text-[var(--rb-gold)]"
            >
              {t.label} ↗
            </a>
          ) : (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
              className={`px-5 py-3 text-[11px] tracking-[0.22em] uppercase transition-all border-b-2 ${tab === t.id ? "border-[var(--rb-gold)] text-[var(--rb-gold)]" : "border-transparent text-[var(--rb-text-muted)] hover:text-[var(--rb-text-heading)]"}`}
            >
              {t.label}
            </button>
          )
        ))}
      </nav>

      {tab === "inquiries" && <InquiriesPanel />}
      {tab === "analytics" && <AnalyticsPanel />}
      {tab === "designs" && <DesignsPanel />}
      {tab === "memorials" && <MemorialsPanel />}
      {tab === "ecards" && <EcardsPanel />}
      {tab === "tools" && <ToolsPanel adminEmail={me.email} />}
    </main>
  );
}

function PayPalModeBadge() {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    apiClient
      .get("/paypal/config")
      .then(({ data }) => setCfg(data))
      .catch(() => setErr(true));
  }, []);

  if (err) {
    return (
      <div
        data-testid="admin-paypal-mode-badge"
        title="Could not read PayPal config from backend"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-400/40 bg-red-500/10 text-red-300 text-[10px] tracking-[0.22em] uppercase"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
        PayPal · Error
      </div>
    );
  }

  if (!cfg) {
    return (
      <div
        data-testid="admin-paypal-mode-badge"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--rb-gold-soft)] text-[var(--rb-text-muted)] text-[10px] tracking-[0.22em] uppercase"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--rb-text-muted)] animate-pulse" />
        PayPal · …
      </div>
    );
  }

  if (!cfg.configured) {
    return (
      <div
        data-testid="admin-paypal-mode-badge"
        title="PayPal credentials are not set on the backend"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-300 text-[10px] tracking-[0.22em] uppercase"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
        PayPal · Not configured
      </div>
    );
  }

  const isLive = String(cfg.mode || "").toLowerCase() === "live";
  const styles = isLive
    ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-300"
    : "border-amber-400/50 bg-amber-500/10 text-amber-300";
  const dot = isLive ? "bg-emerald-400" : "bg-amber-400";
  const label = isLive ? "Live" : "Sandbox";
  const title = isLive
    ? "Backend is using LIVE PayPal credentials — real money will move."
    : "Backend is using SANDBOX PayPal credentials — no real money will move.";

  return (
    <div
      data-testid="admin-paypal-mode-badge"
      title={title}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${styles} text-[10px] tracking-[0.22em] uppercase`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      PayPal · <span data-testid="admin-paypal-mode-value">{label}</span>
    </div>
  );
}

function AnalyticsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/admin/analytics/memorials")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, []);

  const totals = items.reduce((acc, m) => ({
    candles: acc.candles + m.candle_count,
    tributes: acc.tributes + m.tribute_count,
    shares: acc.shares + m.share_clicks,
    downloads: acc.downloads + m.download_clicks,
    clicks: acc.clicks + m.commission_clicks,
  }), { candles: 0, tributes: 0, shares: 0, downloads: 0, clicks: 0 });

  return (
    <div data-testid="admin-analytics">
      <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)] mb-2">Engagement across the Garden</h2>
      <p className="text-[13px] text-[var(--rb-text-muted)] italic mb-6">Quiet metrics — never shown to the families. Just for you, to see what&apos;s moving hearts.</p>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {[
          { label: "Candles lit", v: totals.candles, icon: "🕯" },
          { label: "Tributes left", v: totals.tributes, icon: "✦" },
          { label: "Shares", v: totals.shares, icon: "↗" },
          { label: "Downloads", v: totals.downloads, icon: "⬇" },
          { label: "Commission clicks", v: totals.clicks, icon: "✶" },
        ].map((s, i) => (
          <div key={i} className="glass p-5 rounded-sm text-center">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--rb-text-muted)]">{s.label}</div>
            <div className="font-serif-display text-4xl text-[var(--rb-gold)] mt-2">{s.v}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="text-center font-serif-display italic text-[var(--rb-text-muted)] py-8">Gathering the candles…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-[var(--rb-gold-soft)] bg-white/60">
            <thead className="bg-[var(--rb-gold-soft)]/40">
              <tr>
                <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Memorial</th>
                <th className="p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">🕯 Candles</th>
                <th className="p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">✦ Tributes</th>
                <th className="p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">↗ Shares</th>
                <th className="p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">⬇ Downloads</th>
                <th className="p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">✶ Commission</th>
                <th className="p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Candle→Commission</th>
                <th className="p-3"/>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const pct = (m.conversion_rate * 100).toFixed(0);
                return (
                  <tr key={m.share_slug} className="border-t border-[var(--rb-gold-soft)]/50">
                    <td className="p-3 font-serif-display italic text-base text-[var(--rb-text-heading)]">{m.pet_name}</td>
                    <td className="p-3 text-center">{m.candle_count}</td>
                    <td className="p-3 text-center">{m.tribute_count}</td>
                    <td className="p-3 text-center" title={Object.entries(m.share_channels || {}).map(([k,v]) => `${k}: ${v}`).join(", ")}>{m.share_clicks}</td>
                    <td className="p-3 text-center">{m.download_clicks}</td>
                    <td className="p-3 text-center font-medium text-[var(--rb-gold)]">{m.commission_clicks}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${m.commission_clicks > 0 ? "bg-[#ECFDF5] text-[#0F7A4C]" : "text-[var(--rb-text-muted)]"}`}>
                        {m.candle_count ? `${pct}%` : "—"}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <a href={`/memorial/${m.share_slug}`} target="_blank" rel="noreferrer" className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-gold)]">View</a>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center font-serif-display italic text-[var(--rb-text-muted)]">No engagement to show yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InquiriesPanel() {
  const [items, setItems] = useState([]);
  const load = () => apiClient.get("/admin/inquiries").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await apiClient.patch(`/admin/inquiries/${id}?status=${status}`);
    load();
  };

  const del = async (id) => {
    if (!confirm("Delete inquiry?")) return;
    await apiClient.delete(`/admin/inquiries/${id}`);
    load();
  };

  const statusColors = {
    new: "bg-[#FFFBEB] text-[#92651A] border-[#F5E4B8]",
    in_progress: "bg-[#EFF6FF] text-[#1E4FA8] border-[#BFD7F5]",
    quoted: "bg-[#F4EFFF] text-[#5B3DC7] border-[#D9CFF5]",
    accepted: "bg-[#ECFDF5] text-[#0F7A4C] border-[#B7E8CF]",
    declined: "bg-[#FEF2F2] text-[#9A1F1F] border-[#F4C2C2]",
    completed: "bg-[#F5F5F4] text-[#44403C] border-[#D6D3CF]",
  };

  return (
    <div data-testid="admin-inquiries-list">
      <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)] mb-2">Diorama inquiries ({items.length})</h2>
      <p className="text-[13px] text-[var(--rb-text-muted)] italic mb-6">Each inquiry is also emailed to Theheirloompet@gmail.com — reply directly to that email to start a conversation.</p>
      <div className="space-y-4">
        {items.map((q) => (
          <div key={q.id} className="glass p-6 rounded-sm grid md:grid-cols-12 gap-4 items-start">
            <div className="md:col-span-3">
              <div className="font-serif-display text-xl text-[var(--rb-text-heading)]">{q.customer_name}</div>
              <a href={`mailto:${q.customer_email}`} className="text-[13px] text-[var(--rb-gold)] hover:underline break-all">{q.customer_email}</a>
              {q.customer_phone && <div className="text-[12px] text-[var(--rb-text-muted)] mt-1">{q.customer_phone}</div>}
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] mt-2">{new Date(q.created_at).toLocaleDateString()}</div>
            </div>
            <div className="md:col-span-4">
              {q.pet_name && (
                <>
                  <div className="rb-label !mb-1">Pet</div>
                  <div className="font-serif-display italic text-lg text-[var(--rb-text-heading)]">{q.pet_name}{q.pet_species ? ` · ${q.pet_species}` : ""}</div>
                </>
              )}
              {q.message && (
                <p className="font-serif-display italic text-[14px] text-[var(--rb-text-body)] mt-3 leading-relaxed line-clamp-5">{q.message}</p>
              )}
              {q.memorial_slug && (
                <a href={`/memorial/${q.memorial_slug}`} target="_blank" rel="noreferrer" className="inline-block mt-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-gold)] rainbow-underline">View their memorial →</a>
              )}
            </div>
            <div className="md:col-span-5 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] px-2.5 py-1 rounded-full border tracking-[0.18em] uppercase ${statusColors[q.status] || statusColors.new}`}>
                  {q.status.replace("_", " ")}
                </span>
                {q.deposit_paid && (
                  <span className="text-[10px] px-2.5 py-1 rounded-full border tracking-[0.18em] uppercase bg-[#ECFDF5] text-[#0F7A4C] border-[#B7E8CF]">
                    ✓ ${q.deposit_amount_usd || 50} deposit paid
                  </span>
                )}
                {!q.email_sent && q.email_error && (
                  <span className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">email failed</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {["new", "in_progress", "quoted", "accepted", "declined", "completed"]
                  .filter((s) => s !== q.status)
                  .map((s) => (
                    <button key={s} onClick={() => setStatus(q.id, s)} className="text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 rounded-full border border-[var(--rb-gold-soft)] hover:border-[var(--rb-gold)] hover:bg-white/80 transition">
                      → {s.replace("_", " ")}
                    </button>
                  ))}
              </div>
              <div>
                <button onClick={() => del(q.id)} className="text-[10px] tracking-[0.2em] uppercase text-red-500/70 hover:text-red-600">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-center text-[var(--rb-text-muted)] font-serif-display italic py-12">
            No inquiries yet — they will arrive as visitors share their memorials and click &ldquo;Commission this diorama&rdquo;.
          </p>
        )}
      </div>
    </div>
  );
}

function DesignsPanel() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", customer_name: "", customer_email: "", pet_name: "", notes: "" });
  const [scenery, setScenery] = useState(null);
  const [poem, setPoem] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [sceneryPrompt, setSceneryPrompt] = useState("");
  const [model, setModel] = useState("gemini");

  const load = () => apiClient.get("/admin/designs").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const generateScenery = async () => {
    setGenBusy(true);
    try {
      const { data } = await apiClient.post("/ai/scenery", {
        prompt: sceneryPrompt || `Celestial diorama for ${form.pet_name || "a beloved pet"}`,
        model,
        pet_name: form.pet_name || null,
      }, { timeout: 120000 });
      setScenery({ image_base64: data.image_base64, mime_type: data.mime_type });
    } catch (e) { toast.error("Scenery failed"); }
    finally { setGenBusy(false); }
  };

  const generatePoem = async () => {
    if (!form.pet_name) { toast.error("Pet name needed"); return; }
    setGenBusy(true);
    try {
      const { data } = await apiClient.post("/ai/poem", { pet_name: form.pet_name, traits: form.notes, tone: "gentle" });
      setPoem(data.poem);
    } catch { toast.error("Poem failed"); }
    finally { setGenBusy(false); }
  };

  const save = async () => {
    if (!form.title) { toast.error("Add a title."); return; }
    try {
      await apiClient.post("/admin/designs", {
        ...form,
        poem: poem || null,
        scenery_image_base64: scenery?.image_base64 || null,
        scenery_mime_type: scenery?.mime_type || "image/png",
      });
      toast.success("Design saved.");
      setForm({ title: "", customer_name: "", customer_email: "", pet_name: "", notes: "" });
      setPoem(""); setScenery(null); setSceneryPrompt("");
      setCreating(false);
      load();
    } catch { toast.error("Could not save."); }
  };

  const del = async (id) => {
    if (!confirm("Delete this design?")) return;
    await apiClient.delete(`/admin/designs/${id}`);
    load();
  };

  return (
    <div data-testid={ADMIN.designsList}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)]">Customer commissions</h2>
        <button onClick={() => setCreating((v) => !v)} className="btn-gold">{creating ? "Close" : "+ New design"}</button>
      </div>

      {creating && (
        <div className="glass p-8 mb-8 grid lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div><label className="rb-label">Title *</label><input className="rb-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="The Hazel Diorama"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="rb-label">Customer name</label><input className="rb-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}/></div>
              <div><label className="rb-label">Customer email</label><input className="rb-input" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}/></div>
            </div>
            <div><label className="rb-label">Pet name</label><input className="rb-input" value={form.pet_name} onChange={(e) => setForm({ ...form, pet_name: e.target.value })}/></div>
            <div><label className="rb-label">Notes / traits</label><textarea className="rb-textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></div>
            <div><label className="rb-label">Scenery prompt</label><textarea className="rb-textarea" value={sceneryPrompt} onChange={(e) => setSceneryPrompt(e.target.value)} placeholder="A meadow at golden hour with..."/></div>
            <div className="flex gap-2">
              <button onClick={() => setModel("gemini")} className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase rounded-full border ${model === "gemini" ? "bg-[var(--rb-gold)] text-white" : "border-[var(--rb-gold)]/30"}`}>Gemini</button>
              <button onClick={() => setModel("openai")} className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase rounded-full border ${model === "openai" ? "bg-[var(--rb-gold)] text-white" : "border-[var(--rb-gold)]/30"}`}>OpenAI</button>
            </div>
            <div className="flex gap-3">
              <button onClick={generateScenery} disabled={genBusy} className="btn-ghost flex-1 justify-center">{genBusy ? "…" : "Paint scenery"}</button>
              <button onClick={generatePoem} disabled={genBusy} className="btn-ghost flex-1 justify-center">{genBusy ? "…" : "Write poem"}</button>
            </div>
            <button onClick={save} className="btn-gold w-full justify-center">Save to library</button>
          </div>
          <div>
            {scenery && <img src={`data:${scenery.mime_type};base64,${scenery.image_base64}`} className="w-full rounded-sm border border-[var(--rb-gold)]/30" alt=""/>}
            {poem && (
              <pre className="mt-4 p-4 bg-white/60 rounded-sm font-serif-display italic text-[var(--rb-text-heading)] whitespace-pre-wrap text-[15px] leading-[1.8]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{poem}</pre>
            )}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((d) => (
          <div key={d.id} className="glass p-6 rounded-sm">
            {d.scenery_image_base64 && (
              <img src={`data:${d.scenery_mime_type};base64,${d.scenery_image_base64}`} alt="" className="w-full aspect-square object-cover rounded-sm mb-3"/>
            )}
            <div className="font-serif-display text-xl text-[var(--rb-text-heading)]">{d.title}</div>
            {d.pet_name && <div className="text-xs text-[var(--rb-gold)] tracking-widest uppercase mt-1">{d.pet_name}</div>}
            {d.customer_name && <div className="text-sm text-[var(--rb-text-muted)] mt-2">for {d.customer_name}</div>}
            {d.poem && <p className="font-serif-display italic text-[13px] text-[var(--rb-text-body)] mt-3 line-clamp-4">{d.poem}</p>}
            <button onClick={() => del(d.id)} className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] hover:text-red-500 mt-4">Remove</button>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full text-center text-[var(--rb-text-muted)] font-serif-display italic">No designs yet. Create your first commission ✦</p>}
      </div>
    </div>
  );
}

function MemorialsPanel() {
  const [items, setItems] = useState([]);
  const [recropBusy, setRecropBusy] = useState(false);
  const [recropResult, setRecropResult] = useState(null);
  const load = () => apiClient.get("/admin/memorials").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete memorial?")) return;
    await apiClient.delete(`/admin/memorials/${id}`);
    load();
  };

  const grantForever = async (m) => {
    if (!confirm(`Grant FOREVER hosting to "${m.pet_name}"? This gifts permanent premium status (no payment required) and will show them in the Garden of Remembrance permanently.`)) return;
    try {
      await apiClient.post(`/admin/memorials/${m.share_slug}/grant-forever`);
      toast.success(`${m.pet_name} is sealed in forever light ✦`);
      load();
    } catch (e) {
      toast.error("Could not grant forever hosting.");
    }
  };

  const revokeForever = async (m) => {
    if (!confirm(`Revoke forever hosting from "${m.pet_name}"? This will turn them back into a 3-day free demo starting now.`)) return;
    try {
      await apiClient.post(`/admin/memorials/${m.share_slug}/revoke-forever`);
      toast.success(`${m.pet_name} returned to a fresh 3-day demo.`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not revoke.";
      toast.error(msg);
    }
  };

  const recropFrames = async () => {
    setRecropBusy(true);
    setRecropResult(null);
    try {
      const { data } = await apiClient.post("/admin/maintenance/recrop-frames", {}, { timeout: 120000 });
      setRecropResult(data);
      if (data.cropped > 0) {
        toast.success(`Cleaned ${data.cropped} memorial${data.cropped === 1 ? "" : "s"} ✦`);
        load();
      } else {
        toast.success(`All ${data.checked} memorials are already clean ✦`);
      }
    } catch (e) {
      toast.error("Re-scan failed — check backend logs.");
    } finally {
      setRecropBusy(false);
    }
  };

  const [seedBusy, setSeedBusy] = useState(false);
  const seedEulogies = async (force) => {
    if (force && !confirm("Overwrite the founding pets' eulogies with the canonical versions? This will replace any edits.")) return;
    setSeedBusy(true);
    try {
      const { data } = await apiClient.post("/admin/maintenance/seed-founding-eulogies", { force: !!force }, { timeout: 20000 });
      if (data.seeded > 0) {
        toast.success(`${force ? "Overwrote" : "Seeded"} ${data.seeded} of ${data.checked} eulogies ✦`);
        load();
      } else if (data.not_found > 0) {
        toast.error(`${data.not_found} founding memorial(s) missing from this DB.`);
      } else {
        toast.success(`All ${data.checked} founding eulogies already in place ✦`);
      }
    } catch (e) {
      toast.error("Eulogy seed failed — check backend logs.");
    } finally {
      setSeedBusy(false);
    }
  };

  return (
    <div data-testid={ADMIN.memorialsList}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)]">Public memorials ({items.length})</h2>
        <div className="flex items-center gap-3">
          {recropResult && (
            <span className="text-[11px] tracking-[0.18em] uppercase text-[var(--rb-text-muted)]" data-testid="recrop-result-summary">
              {recropResult.cropped > 0
                ? `Cleaned ${recropResult.cropped} of ${recropResult.checked}`
                : `${recropResult.checked} already clean`}
            </span>
          )}
          <button
            onClick={recropFrames}
            disabled={recropBusy}
            className="btn-ghost text-[10px] tracking-[0.2em] uppercase disabled:opacity-50"
            data-testid="admin-recrop-frames-btn"
            title="Re-scan all memorial images and remove any wooden-frame artifacts"
          >
            {recropBusy ? "Scanning…" : "↻ Refresh memorial images"}
          </button>
          <button
            onClick={() => seedEulogies(false)}
            disabled={seedBusy}
            className="btn-ghost text-[10px] tracking-[0.2em] uppercase disabled:opacity-50"
            data-testid="admin-seed-eulogies-btn"
            title="Ensure the founding pets (Little Bear, Abby, Princess) have their canonical eulogies"
          >
            {seedBusy ? "Seeding…" : "✦ Seed founding eulogies"}
          </button>
          <button
            onClick={() => seedEulogies(true)}
            disabled={seedBusy}
            className="btn-ghost text-[10px] tracking-[0.2em] uppercase disabled:opacity-50 text-red-600 border-red-300 hover:text-red-800"
            data-testid="admin-seed-eulogies-force-btn"
            title="Force overwrite founding pets' eulogies with the canonical versions (destructive)"
          >
            ↺ Force
          </button>
        </div>
      </div>

      {recropResult && recropResult.cropped > 0 && (
        <div className="glass p-4 mb-6 rounded-sm text-[12px] text-[var(--rb-text-body)]" data-testid="recrop-result-detail">
          <div className="tracking-celestial text-[var(--rb-gold)] mb-2">Just cleaned</div>
          <ul className="space-y-1 font-serif-display italic">
            {recropResult.details.map((d) => (
              <li key={d.share_slug}>
                <span className="text-[var(--rb-text-heading)]">{d.pet_name}</span>
                <span className="text-[var(--rb-text-muted)]"> — {d.before} → {d.after}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-[var(--rb-gold-soft)] bg-white/60">
          <thead className="bg-[var(--rb-gold-soft)]/40">
            <tr>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Pet</th>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Family</th>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Dates</th>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Status</th>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Created</th>
              <th className="p-3"/>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => {
              const isFounder = FOUNDER_SLUGS.has(m.share_slug);
              return (
                <tr key={m.id} className="border-t border-[var(--rb-gold-soft)]/50">
                  <td className="p-3 font-serif-display italic text-base text-[var(--rb-text-heading)]">{m.pet_name}</td>
                  <td className="p-3">{m.owner_display_name || "—"}</td>
                  <td className="p-3 text-[var(--rb-text-muted)]">{m.dates || "—"}</td>
                  <td className="p-3">
                    {isFounder ? (
                      <span className="inline-block px-2 py-1 text-[9px] tracking-[0.18em] uppercase rounded-full bg-[var(--rb-gold-soft)]/60 text-[var(--rb-gold)] border border-[var(--rb-gold)]/40" title="Original founding memorial">
                        ✦ Founder
                      </span>
                    ) : m.is_premium ? (
                      <span className="inline-block px-2 py-1 text-[9px] tracking-[0.18em] uppercase rounded-full bg-[var(--rb-gold)] text-white" title="Permanent premium hosting">
                        ✦ Forever
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-1 text-[9px] tracking-[0.18em] uppercase rounded-full bg-white/70 text-[var(--rb-text-muted)] border border-[var(--rb-gold-soft)]" title={m.expires_at ? `Expires ${new Date(m.expires_at).toLocaleString()}` : "Demo"}>
                        3-day demo
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-[var(--rb-text-muted)]">{new Date(m.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <a href={`/memorial/${m.share_slug}`} target="_blank" rel="noreferrer" className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-gold)] mr-3">View</a>
                    {!isFounder && !m.is_premium && (
                      <button
                        onClick={() => grantForever(m)}
                        className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-gold)] hover:text-[var(--rb-gold-deep)] mr-3"
                        data-testid={`admin-grant-forever-${m.share_slug}`}
                        title="Gift permanent hosting to this memorial (no payment required)"
                      >
                        ✦ Grant forever
                      </button>
                    )}
                    {!isFounder && m.is_premium && (
                      <button
                        onClick={() => revokeForever(m)}
                        className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] hover:text-red-500 mr-3"
                        data-testid={`admin-revoke-forever-${m.share_slug}`}
                        title="Revoke forever hosting (revert to a fresh 3-day demo)"
                      >
                        ↺ Revoke
                      </button>
                    )}
                    {!isFounder && (
                      <button onClick={() => del(m.id)} className="text-[10px] tracking-[0.2em] uppercase text-red-500/70 hover:text-red-500">Delete</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center font-serif-display italic text-[var(--rb-text-muted)]">No memorials yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EcardsPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { apiClient.get("/admin/ecards").then(({ data }) => setItems(data)); }, []);

  return (
    <div data-testid={ADMIN.ecardsList}>
      <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)] mb-6">eCards ({items.length})</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-[var(--rb-gold-soft)] bg-white/60">
          <thead className="bg-[var(--rb-gold-soft)]/40">
            <tr>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">From → To</th>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Pet</th>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Template</th>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Email</th>
              <th className="text-left p-3 text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Created</th>
              <th className="p-3"/>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-[var(--rb-gold-soft)]/50">
                <td className="p-3">{c.sender_name} → {c.recipient_name}</td>
                <td className="p-3 font-serif-display italic">{c.pet_name || "—"}</td>
                <td className="p-3 text-xs">{c.template}</td>
                <td className="p-3 text-xs">{c.email_sent ? "✓ sent" : c.email_error ? "× failed" : "—"}</td>
                <td className="p-3 text-[var(--rb-text-muted)]">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right">
                  <a href={`/ecard/${c.share_slug}`} target="_blank" rel="noreferrer" className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-gold)]">View</a>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center font-serif-display italic text-[var(--rb-text-muted)]">No eCards yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ToolsPanel({ adminEmail }) {
  const [recipient, setRecipient] = useState(adminEmail || "");
  const [busy, setBusy] = useState(null); // 'ecard' | 'inquiry' | null
  const [history, setHistory] = useState([]); // {type, ok, from, to, message_id, error, at}

  // PayPal invoice tool
  const [invAmount, setInvAmount] = useState("");
  const [invDesc, setInvDesc] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invBusy, setInvBusy] = useState(false);
  const [invLink, setInvLink] = useState(null);

  const send = async (type) => {
    if (!recipient || !recipient.includes("@")) {
      toast.error("Enter a valid recipient email.");
      return;
    }
    setBusy(type);
    try {
      const { data } = await apiClient.post(
        "/admin/maintenance/test-email",
        { type, recipient_email: recipient },
        { timeout: 60000 }
      );
      setHistory((h) => [{ ...data, at: new Date() }, ...h].slice(0, 8));
      if (data.ok) {
        toast.success(`Test ${type === "ecard" ? "eCard" : "inquiry"} sent ✦`);
      } else {
        toast.error(`Send failed: ${data.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test email.");
    } finally {
      setBusy(null);
    }
  };

  const generateInvoice = async () => {
    const amt = parseFloat(invAmount);
    if (!Number.isFinite(amt) || amt < 1) {
      toast.error("Enter an amount ≥ $1.");
      return;
    }
    if (!invDesc.trim()) {
      toast.error("Add a short description (what the invoice is for).");
      return;
    }
    setInvBusy(true);
    setInvLink(null);
    try {
      const { data } = await apiClient.post("/paypal/checkout/invoice", {
        amount_usd: amt,
        description: invDesc,
        customer_email: invEmail || null,
        origin_url: window.location.origin,
      });
      setInvLink(data.approval_url);
      toast.success("PayPal invoice link created ✦");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invoice.");
    } finally {
      setInvBusy(false);
    }
  };

  return (
    <div data-testid="admin-tools-panel">
      <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)] mb-2">Studio tools</h2>
      <p className="text-[13px] text-[var(--rb-text-muted)] italic mb-8">Owner-only utilities: printable keepsakes, email tests, PayPal invoices.</p>

      <a
        href="/admin/keepsake-cards"
        className="block glass p-8 rounded-sm max-w-3xl mb-6 hover:border-[var(--rb-gold)] transition-colors group"
        data-testid="tools-keepsake-cards-link"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="tracking-celestial text-[var(--rb-gold)] mb-2">Printable Keepsake Cards</div>
            <h3 className="font-serif-display italic text-2xl text-[var(--rb-text-heading)]">8×10 memorial cards for cardstock printing</h3>
            <p className="text-[13px] text-[var(--rb-text-body)] mt-3 leading-relaxed">
              Craft watercolor floral keepsakes for Etsy orders or custom gifts — watercolor pet portraits
              in flower bouquets, printed on 8.5×11 letter cardstock. Save drafts to reprint later.
            </p>
          </div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[var(--rb-gold)] whitespace-nowrap group-hover:translate-x-1 transition-transform">Open ✦</div>
        </div>
      </a>

      <div className="glass p-8 rounded-sm max-w-3xl">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-3">Email deliverability test</div>
        <p className="text-[13px] text-[var(--rb-text-body)] mb-5 leading-relaxed">
          Sends a sample <em>eCard</em> or <em>commission inquiry</em> email to the address below — useful after
          changing your sending domain, adjusting templates, or just confirming that production
          is still delivering mail.
        </p>

        <label className="rb-label">Send test emails to</label>
        <input
          className="rb-input"
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="you@yourdomain.com"
          data-testid="tools-test-recipient-input"
        />

        <div className="flex flex-wrap gap-3 mt-5">
          <button
            onClick={() => send("ecard")}
            disabled={busy !== null}
            className="btn-gold disabled:opacity-50"
            data-testid="tools-send-test-ecard-btn"
          >
            {busy === "ecard" ? "Sending eCard…" : "Send sample eCard"}
          </button>
          <button
            onClick={() => send("inquiry")}
            disabled={busy !== null}
            className="btn-ghost disabled:opacity-50"
            data-testid="tools-send-test-inquiry-btn"
          >
            {busy === "inquiry" ? "Sending inquiry…" : "Send sample inquiry"}
          </button>
        </div>

        {history.length > 0 && (
          <div className="mt-8 pt-6 border-t border-[var(--rb-gold-soft)]" data-testid="tools-history">
            <div className="tracking-celestial text-[10px] text-[var(--rb-text-muted)] mb-3">Recent test sends</div>
            <ul className="space-y-2 text-[12px]">
              {history.map((h, i) => (
                <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className={`inline-block w-2 h-2 rounded-full ${h.ok ? "bg-[#0F7A4C]" : "bg-red-500"}`} />
                  <span className="font-serif-display italic text-[var(--rb-text-heading)]">{h.type === "ecard" ? "eCard" : "Inquiry"}</span>
                  <span className="text-[var(--rb-text-muted)]">→ {h.to}</span>
                  <span className="text-[var(--rb-text-muted)] text-[10px] tracking-[0.18em] uppercase">{h.at.toLocaleTimeString()}</span>
                  {h.ok ? (
                    <span className="text-[var(--rb-text-muted)] text-[10px] truncate">id {h.message_id || "—"}</span>
                  ) : (
                    <span className="text-red-600 text-[11px]">× {h.error}</span>
                  )}
                </li>
              ))}
            </ul>
            <div className="text-[10px] text-[var(--rb-text-muted)] italic mt-4">
              Sender: <strong className="not-italic text-[var(--rb-text-heading)]">{history[0]?.from}</strong>
            </div>
          </div>
        )}
      </div>

      <div className="glass p-8 rounded-sm max-w-3xl mt-8" data-testid="paypal-invoice-panel">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-3">PayPal invoice generator</div>
        <p className="text-[13px] text-[var(--rb-text-body)] mb-5 leading-relaxed">
          Create a one-time PayPal payment link for any amount — final balances, add-ons,
          extras. Copy the link and email it to the customer. Successful payments land them
          on the Garden of Remembrance.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="rb-label">Amount (USD)</label>
            <input
              type="number"
              min="1"
              step="1"
              className="rb-input"
              value={invAmount}
              onChange={(e) => setInvAmount(e.target.value)}
              placeholder="e.g. 150"
              data-testid="invoice-amount-input"
            />
          </div>
          <div>
            <label className="rb-label">Customer email (optional, for your records)</label>
            <input
              type="email"
              className="rb-input"
              value={invEmail}
              onChange={(e) => setInvEmail(e.target.value)}
              placeholder="customer@example.com"
              data-testid="invoice-email-input"
            />
          </div>
        </div>
        <label className="rb-label mt-1">Description</label>
        <input
          type="text"
          className="rb-input"
          value={invDesc}
          onChange={(e) => setInvDesc(e.target.value)}
          placeholder="e.g. Final balance for Bella's diorama"
          maxLength={127}
          data-testid="invoice-desc-input"
        />

        <button
          onClick={generateInvoice}
          disabled={invBusy}
          className="btn-gold mt-5 disabled:opacity-50"
          data-testid="invoice-generate-btn"
        >
          {invBusy ? "Creating…" : "Generate PayPal invoice link"}
        </button>

        {invLink && (
          <div className="mt-6 p-4 border border-[var(--rb-gold)]/30 rounded-sm bg-[var(--rb-cream)]" data-testid="invoice-link-result">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] mb-2">Copy this link and email it to the customer</div>
            <div className="flex items-center gap-3 flex-wrap">
              <a href={invLink} target="_blank" rel="noopener noreferrer" className="text-[13px] text-[var(--rb-gold)] underline break-all font-mono" data-testid="invoice-link-anchor">
                {invLink}
              </a>
              <button
                onClick={() => { navigator.clipboard.writeText(invLink); toast.success("Copied ✦"); }}
                className="btn-ghost text-[10px] tracking-[0.2em] uppercase"
                data-testid="invoice-copy-btn"
              >
                Copy link
              </button>
            </div>
            <p className="text-[10px] text-[var(--rb-text-muted)] italic mt-3">
              This link expires when the customer completes payment. Track its status in your PayPal dashboard.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

